import { RefreshButton } from "@/components/admin/refresh-button";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { UserMenu } from "@/components/admin/user-menu";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import {
  Sidebar as SidebarPrimitive,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Briefcase,
  Building2,
  Calendar,
  CalendarCheck,
  ChevronRight,
  ClipboardList,
  AlertCircle,
  Calculator,
  CalendarDays,
  Clock,
  FileSearch,
  FileText,
  Hash,
  Import,
  Layers,
  LayoutDashboard,
  Link2,
  LogOut,
  Package,
  PanelLeft,
  PanelLeftClose,
  Receipt,
  RotateCcw,
  Settings,
  Shield,
  ShieldAlert,
  ShoppingCart,
  TrendingUp,
  Truck,
  User,
  UserCircle,
  Users,
  Wallet,
  Wrench,
} from "lucide-react";
import { CanAccess, useLogout, useUserMenu } from "ra-core";
import { useState } from "react";
import { Link, useLocation } from "react-router";
import { ImportPage } from "../misc/ImportPage";
import { useConfigurationContext } from "../root/ConfigurationContext";

// --- Menu Component Implementations from former Header.tsx ---
const UsersMenu = () => {
  const userMenuContext = useUserMenu();
  if (!userMenuContext) return null;
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/sales" className="flex items-center gap-2">
        <Users className="w-4 h-4" /> Users
      </Link>
    </DropdownMenuItem>
  );
};

const ProfileMenu = () => {
  const userMenuContext = useUserMenu();
  if (!userMenuContext) return null;
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/profile" className="flex items-center gap-2">
        <User className="w-4 h-4" />
        Profile
      </Link>
    </DropdownMenuItem>
  );
};

const SettingsMenu = () => {
  const userMenuContext = useUserMenu();
  if (!userMenuContext) return null;
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/settings" className="flex items-center gap-2">
        <Settings className="w-4 h-4" /> Settings
      </Link>
    </DropdownMenuItem>
  );
};

const ImportFromJsonMenuItem = () => {
  const userMenuContext = useUserMenu();
  if (!userMenuContext) return null;
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ImportPage.path} className="flex items-center gap-2">
        <Import className="w-4 h-4" /> Import data
      </Link>
    </DropdownMenuItem>
  );
};

interface MenuItem {
  label: string;
  icon: any;
  to: string;
}

interface MenuSection {
  label: string;
  items: MenuItem[];
}

const menuSections: MenuSection[] = [
  {
    label: "CRM",
    items: [
      { label: "Leads", icon: UserCircle, to: "/contacts" },
      { label: "Enquiries", icon: ClipboardList, to: "/enquiries" },
      { label: "Opportunities", icon: TrendingUp, to: "/opportunities" },
      { label: "Projects", icon: Briefcase, to: "/projects" },
      { label: "Companies", icon: Building2, to: "/companies" },
    ],
  },
  {
    label: "Sales",
    items: [
      { label: "Global Ledger", icon: TrendingUp, to: "/sales" },
      { label: "Auto Invoice", icon: FileText, to: "/dispatch_to_invoice_queue" },
      { label: "Customer Aging", icon: AlertCircle, to: "/customer_aging_view" },
      { label: "Credit Limit", icon: ShieldAlert, to: "/credit-limits" },
      { label: "PDC Tracking", icon: Clock, to: "/pdc_cheques" },
      { label: "Credit Notes", icon: RotateCcw, to: "/credit_notes" },
      { label: "Receipt Matching", icon: Link2, to: "/payment_receipts" },
    ],
  },
  {
    label: "Documents",
    items: [
      { label: "Quotations", icon: FileText, to: "/quotations" },
      { label: "Sales Orders", icon: FileText, to: "/sales_orders" },
      {
        label: "Purchase Orders",
        icon: ShoppingCart,
        to: "/purchase_orders",
      },
      { label: "Proforma Invoice", icon: Receipt, to: "/proforma_invoices" },
      { label: "Invoices", icon: Receipt, to: "/customer_invoices" },
      { label: "Delivery Challan", icon: Truck, to: "/delivery_challans" },
    ],
  },
  {
    label: "Design & Production",
    items: [
      { label: "Design Requests", icon: Wrench, to: "/design_requests" },
      { label: "Bill of Materials", icon: Layers, to: "/boms" },
      { label: "RFQs", icon: FileSearch, to: "/rfqs" },
      { label: "Work Orders", icon: Package, to: "/work_orders" },
    ],
  },
  {
    label: "Operations",
    items: [
      { label: "Inventory", icon: Package, to: "/inventory_items" },
      { label: "HSN / SAC Codes", icon: Hash, to: "/hsn_codes" },
      { label: "Service Tickets", icon: Wrench, to: "/service_tickets" },
    ],
  },
  {
    label: "Procurement",
    items: [
      { label: "Vendors", icon: Users, to: "/vendors" },
      { label: "Goods Receipts", icon: Package, to: "/goods_receipts" },
      { label: "Vendor Bills", icon: Receipt, to: "/vendor_bills" },
    ],
  },
  {
    label: "HR",
    items: [
      { label: "Employees", icon: Users, to: "/employees" },
      { label: "Departments", icon: Building2, to: "/hr_departments" },
      { label: "Designations", icon: Briefcase, to: "/hr_designations" },
      { label: "Attendance", icon: Clock, to: "/hr_attendance" },
      { label: "Leave Types", icon: CalendarDays, to: "/hr_leave_types" },
      {
        label: "Leave Requests",
        icon: CalendarCheck,
        to: "/hr_leave_requests",
      },
      { label: "Holidays", icon: Calendar, to: "/hr_holidays" },
      {
        label: "Payroll Components",
        icon: Calculator,
        to: "/hr_payroll_components",
      },
      {
        label: "Salary Structures",
        icon: Wallet,
        to: "/hr_salary_structures",
      },
      { label: "Payroll", icon: Receipt, to: "/hr_payroll_runs" },
    ],
  },
  {
    label: "Administration",
    items: [
      { label: "Users", icon: Users, to: "/users" },
      { label: "Roles & Access", icon: Shield, to: "/user-roles" },
      { label: "Permissions", icon: Shield, to: "/permissions" },
    ],
  },
];

const defaultExpandedSections: Record<string, boolean> = {
  crm: true,
  sales: true,
  documents: true,
  "design_&_production": false,
  operations: false,
  procurement: false,
  hr: false,
  administration: false,
};

const SidebarBody = () => {
  const location = useLocation();
  const logout = useLogout();
  const { state, toggleSidebar } = useSidebar();
  const isMinimized = state === "collapsed";

  // Persisted so a page refresh doesn't collapse whichever section the
  // user had open — previously reset to the defaults above on every reload.
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >(() => {
    try {
      const stored = localStorage.getItem("sidebar_expanded_sections");
      return stored
        ? { ...defaultExpandedSections, ...JSON.parse(stored) }
        : defaultExpandedSections;
    } catch {
      return defaultExpandedSections;
    }
  });

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = { ...prev, [section]: !prev[section] };
      try {
        localStorage.setItem("sidebar_expanded_sections", JSON.stringify(next));
      } catch {
        // localStorage unavailable (private mode etc.) — non-fatal
      }
      return next;
    });
  };

  const isItemActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    // Ensure exact match or subpath match, preventing greedy partial prefix matches
    return location.pathname === to || location.pathname.startsWith(to + "/");
  };

  return (
    <SidebarPrimitive collapsible="icon" className="hidden md:flex">
      <SidebarHeader className="p-4 flex-row items-center justify-between gap-2 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:gap-3">
        <Link
          to="/"
          className="flex items-center gap-2 no-underline overflow-hidden shrink-0 group-data-[collapsible=icon]:justify-center"
        >
          <img
            src="/logos/rovasys_200.png"
            alt="Rovasys"
            className="h-9 w-9 rounded-lg object-contain shrink-0 hidden group-data-[collapsible=icon]:block dark:brightness-0 dark:invert"
          />
          <img
            src="/logos/rovasys_horizontal_clean.png"
            alt="Rovasys"
            className="h-8 w-auto object-contain shrink-0 group-data-[collapsible=icon]:hidden dark:brightness-0 dark:invert"
          />
        </Link>
        <button
          onClick={toggleSidebar}
          className="p-1.5 hover:bg-sidebar-accent rounded-md text-sidebar-foreground/70 transition-colors shrink-0"
          title={isMinimized ? "Expand Sidebar" : "Minimize Sidebar"}
        >
          {isMinimized ? <PanelLeft size={20} /> : <PanelLeftClose size={20} />}
        </button>
      </SidebarHeader>

      <SidebarContent className="scrollbar-hide px-1">
        <SidebarGroup className="py-0.5">
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={location.pathname === "/"}
                  tooltip="Dashboard"
                >
                  <Link to="/">
                    <LayoutDashboard size={20} className="shrink-0" />
                    <span className="group-data-[collapsible=icon]:hidden">Dashboard</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator />

        {menuSections.map((section) => {
          const sectionKey = section.label.toLowerCase().replace(/\s+/g, "_");
          const isExpanded = expandedSections[sectionKey];
          const SectionIcon = section.items[0].icon;

          return (
            <SidebarGroup key={section.label} className="py-0.5">
              <SidebarGroupLabel
                asChild
                className="cursor-pointer text-sidebar-foreground/90 font-semibold uppercase tracking-wider group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:opacity-100 group-data-[collapsible=icon]:mt-0"
              >
                <button className="text-left" onClick={() => toggleSection(sectionKey)} title={isMinimized ? section.label : undefined}>
                  <span className="hidden group-data-[collapsible=icon]:inline">
                    <SectionIcon size={16} />
                  </span>
                  <span className="flex-1 text-left group-data-[collapsible=icon]:hidden">{section.label}</span>
                  <ChevronRight
                    size={14}
                    className={`transition-transform duration-200 ease-in-out group-data-[collapsible=icon]:hidden ${isExpanded ? "rotate-90" : ""}`}
                  />
                </button>
              </SidebarGroupLabel>

              <div
                className={`grid transition-[grid-template-rows] duration-200 ease-in-out ${isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
              >
                <div className="overflow-hidden">
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {section.items.map((item) => (
                        <SidebarMenuItem key={item.label}>
                          <SidebarMenuButton
                            asChild
                            isActive={isItemActive(item.to)}
                            tooltip={item.label}
                            className="pl-4 group-data-[collapsible=icon]:pl-2"
                          >
                            <Link to={item.to}>
                              <item.icon size={18} className="shrink-0" />
                              <span className="group-data-[collapsible=icon]:hidden">{item.label}</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </div>
              </div>
            </SidebarGroup>
          );
        })}

        <SidebarSeparator />

        <SidebarGroup className="py-0.5">
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isItemActive("/settings")} tooltip="Settings">
                  <Link to="/settings">
                    <Settings size={20} className="shrink-0" />
                    <span className="group-data-[collapsible=icon]:hidden">Settings</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border">
        <div className="flex items-center justify-between px-1 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:gap-2">
          <div className="flex items-center gap-1 group-data-[collapsible=icon]:flex-col">
            <ThemeModeToggle />
            <RefreshButton />
          </div>
          <UserMenu>
            <ProfileMenu />
            <CanAccess resource="sales" action="list">
              <UsersMenu />
            </CanAccess>
            <CanAccess resource="configuration" action="edit">
              <SettingsMenu />
            </CanAccess>
            <ImportFromJsonMenuItem />
          </UserMenu>
        </div>

        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => logout()}
              tooltip="Logout"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <LogOut size={20} className="shrink-0" />
              <span className="group-data-[collapsible=icon]:hidden">Logout</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </SidebarPrimitive>
  );
};

export const Sidebar = () => {
  useConfigurationContext();
  return <SidebarBody />;
};
